import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faClose, faGear, faMessage, faSearch } from "@fortawesome/free-solid-svg-icons"
import type { QuickSearchAction, QuickSearchActionType } from "../../configuration/QuickSearchActions"
import { useEmployerLogged } from "../../context/useEmployeeContext"
import { useCompanyModulesStatus } from "../../context/useCompanyModulesStatusContext"
import InputField from "../InputField/InputField"
import ModalPortal from "../Modal/ModalPortal"
import defaultStyle from "../../styles/defaultPageStyle.module.css"
import { TitlePageResponsiveIconSmall } from "../Titles/Titles"
import { ButtonIcon } from "../Button/Button"
import THEME from "../../configuration/Theme"
import { SIZE } from "../../configuration/Sizes"
import Animate, { AnimationTypes } from "../Animate/Animate"
import { getAvailableQuickSearchActions } from "../../utils/quickSearchActions"
import { buildQuickSearchActionSearch } from "../../utils/quickSearchNavigation"
import { quickSearchActionTypeConfigs } from "../../utils/quickSearchActionTypeConfig"
import GetSpace from "../../utils/space"

type QuickSearchPopupProps = {
  onClose: () => void
  onNavigate?: () => void
  onOpenOperationalHelp?: () => void
}

const normalizeSearchText = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()

export const QuickSearchPopup: React.FC<QuickSearchPopupProps> = ({ onClose, onNavigate, onOpenOperationalHelp }) => {
  const { t } = useTranslation("quickSearch")

  const navigate = useNavigate()

  const location = useLocation()

  const { userEmployee } = useEmployerLogged()

  const { isModuleEnabled } = useCompanyModulesStatus()

  const [search, setSearch] = useState("")

  const [selectedType, setSelectedType] = useState<QuickSearchActionType | null>(null)

  const availableActions = useMemo(
    () => getAvailableQuickSearchActions(userEmployee, isModuleEnabled),
    [isModuleEnabled, userEmployee],
  )

  const filteredActions = useMemo(() => {
    const normalizedSearch = normalizeSearchText(search)

    const actionsByType = selectedType
      ? availableActions.filter((action) => action.type === selectedType)
      : availableActions

    if (!normalizedSearch || selectedType) {
      return actionsByType
    }

    return actionsByType.filter((action) => {
      const keywords = t(`actions.${action.id}.keywords`, { returnObjects: true }) as string[]

      return normalizeSearchText(
        [
          t(`actions.${action.id}.title`),
          t(`actions.${action.id}.description`),
          t(`groups.${action.group}`),
          t(`types.${action.type}`),
          ...(Array.isArray(keywords) ? keywords : []),
        ].join(" "),
      ).includes(normalizedSearch)
    })
  }, [availableActions, search, selectedType, t])

  const groupedActions = useMemo(() => {
    return filteredActions.reduce<Record<QuickSearchActionType, QuickSearchAction[]>>(
      (acc, action) => {
        acc[action.type].push(action)

        return acc
      },
      { create: [], edit: [], view: [], organize: [] },
    )
  }, [filteredActions])

  const actionTypesOrder: QuickSearchActionType[] =
    search.trim().length > 0 ? ["create", "edit", "organize", "view"] : ["create", "edit", "view", "organize"]

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose()
      }
    }

    window.addEventListener("keydown", handleKeyDown)

    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [onClose])

  const handleNavigate = (action: QuickSearchAction) => {
    onNavigate?.()

    navigate({
      pathname: action.path,
      search: buildQuickSearchActionSearch(action, location.search),
    })

    onClose()
  }

  const handleTypeFilter = (type: QuickSearchActionType) => {
    setSelectedType(type)
    setSearch(t(`types.${type}`))
  }

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedType(null)
    setSearch(event.target.value)
  }

  const handleOpenOperationalHelp = () => {
    onOpenOperationalHelp?.()
    onClose()
  }

  const renderAction = (action: QuickSearchAction) => {
    const typeConfig = quickSearchActionTypeConfigs[action.type]

    return (
      <button
        key={action.id}
        type="button"
        onClick={() => handleNavigate(action)}
        className="
          group flex w-full items-start gap-3 rounded-lg border border-slate-200
          bg-white px-3 py-3 text-left shadow-sm transition
          hover:-translate-y-0.5 hover:border-blue-200 hover:bg-blue-50 hover:shadow-md
          focus:bg-blue-50 focus:outline-none
        "
        data-interaction-description-key="interactionDescriptions:components.quickSearch.quickSearchPopup.button.handleNavigateAction"
      >
        <span
          className={`
            mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg shadow-sm
            ${typeConfig.iconClassName}
          `}
        >
          <FontAwesomeIcon icon={typeConfig.icon} />
        </span>

        <span className="min-w-0 flex-1">
          <span className="block text-sm font-black leading-tight text-slate-900">
            {t(`actions.${action.id}.title`)}
          </span>

          <span className="mt-1 block text-xs font-semibold leading-relaxed text-slate-500">
            {t(`actions.${action.id}.description`)}
          </span>

          <span className="mt-2 flex flex-wrap gap-2">
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-600">
              {t(`groups.${action.group}`)}
            </span>

            <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${typeConfig.badgeClassName}`}>
              {t(`types.${action.type}`)}
            </span>
          </span>
        </span>
      </button>
    )
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.25s">
          <div className={defaultStyle.alert2}>
            <TitlePageResponsiveIconSmall
              title={t("title")}
              icon={faSearch}
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className={defaultStyle.ButtonsActions}>
                  {onOpenOperationalHelp && (
                    <ButtonIcon
                      sizeNumber={SIZE.buttonCloseIconSize}
                      bgColor="#2563eb"
                      iconColor="#ffffff"
                      icon={faMessage}
                      onClick={handleOpenOperationalHelp}
                      size="small"
                      descriptionKey="interactionDescriptions:components.quickSearch.quickSearchPopup.button.openOperationalHelp"
                    />
                  )}

                  <GetSpace left={5} />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    size="small"
                    descriptionKey="interactionDescriptions:components.quickSearch.quickSearchPopup.button.onClose"
                  />
                </div>
              }
            />

            <section className="p-2">
              <InputField
                id="quick-search-input"
                type="text"
                value={search}
                icon={faSearch}
                placeholder={t("placeholder")}
                onInvalidMessage={t("invalid")}
                onChange={handleSearchChange}
                descriptionKey="interactionDescriptions:components.quickSearch.quickSearchPopup.input.placeholder"
              />

              <div className="mt-3 flex flex-wrap gap-2">
                {(["create", "edit", "view", "organize"] as QuickSearchActionType[]).map((type, index) => {
                  const typeConfig = quickSearchActionTypeConfigs[type]
                  const isActive = selectedType === type

                  return (
                    <Animate key={index} type={AnimationTypes.fadeIn} duration={1.5 + index * 0.4 + "s"}>
                      <button
                        key={type}
                        type="button"
                        onClick={() => handleTypeFilter(type)}
                        className={`
                        inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-black
                        transition hover:-translate-y-0.5 hover:shadow-sm
                        ${typeConfig.badgeClassName}
                        ${isActive ? "outline outline-2 outline-offset-2 outline-blue-400" : ""}
                      `}
                        data-interaction-description-key="interactionDescriptions:components.quickSearch.quickSearchPopup.button.handleTypeFilterType"
                      >
                        <FontAwesomeIcon icon={typeConfig.icon} />

                        {t(`types.${type}`)}
                      </button>
                    </Animate>
                  )
                })}
              </div>
            </section>

            <div className={defaultStyle.divisionBar}></div>

            <section className="max-h-[55dvh] overflow-y-auto px-2 pb-2">
              {filteredActions.length === 0 && (
                <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
                  <div className="px-4 py-10 text-center">
                    <FontAwesomeIcon icon={faGear} className="text-2xl text-slate-400" />

                    <p className="mt-2 text-sm font-bold text-slate-700">{t("empty_title")}</p>

                    <p className="mt-1 text-xs font-semibold text-slate-500">{t("empty_description")}</p>
                  </div>
                </Animate>
              )}

              {actionTypesOrder.map((type, index) => {
                const actions = groupedActions[type]

                if (actions.length === 0) {
                  return null
                }

                return (
                  <Animate key={type} type={AnimationTypes.fadeInTraslate} duration={2 + index * 0.5 + "s"}>
                    <div key={type} className="mb-4">
                      <div className="sticky top-0 z-10 flex items-center justify-between bg-white/95 py-2 backdrop-blur">
                        <h3 className="text-xs font-black uppercase text-slate-700">{t(`sections.${type}`)}</h3>

                        <span className="text-[11px] font-bold text-slate-400">
                          {actions.length} {actions.length === 1 ? t("result") : t("results")}
                        </span>
                      </div>

                      <div className="grid grid-cols-1 gap-2 lg:grid-cols-2">{actions.map(renderAction)}</div>
                    </div>
                  </Animate>
                )
              })}
            </section>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
